import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/logo-servicemind24-small.webp";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, language, setLanguage } = useLanguage();

  const navItems = [
    { name: t("nav.home"), href: "/", type: "link" },
    { name: t("nav.services"), href: "#services", type: "scroll" },
    { name: t("nav.about"), href: "/about", type: "link" },
    { name: t("nav.contact"), href: "#contact", type: "scroll" },
    { name: t("nav.members"), href: "/members", type: "link" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-border shadow-sm">
      <div className="container mx-auto px-4 max-w-full">
        {/* Main navigation */}
        <div className="flex items-center justify-between py-3">
          <Link to="/" className="flex flex-col items-center gap-1">
            <img 
              src={logo} 
              alt="ServiceMind24 Logo" 
              className="h-28 w-28 object-contain"
              width="112"
              height="112"
              loading="eager"
            />
            <p className="text-sm text-muted-foreground italic">
              "{t("header.tagline")}"
            </p>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              item.type === "link" ? (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-foreground hover:text-primary transition-smooth font-medium"
                >
                  {item.name}
                </Link>
              ) : (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-foreground hover:text-primary transition-smooth font-medium"
                >
                  {item.name}
                </a>
              )
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <Globe className="h-4 w-4" />
                  {language === "en" ? "EN" : "FI"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-background">
                <DropdownMenuItem onClick={() => setLanguage("en")}>
                  🇬🇧 English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage("fi")}>
                  🇫🇮 Suomi
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <a 
              href="https://wa.me/358402401909"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
            >
              📱 WhatsApp
            </a>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('tel:0931588300', '_self')}
              className="whitespace-nowrap"
            >
              <Phone className="h-4 w-4 mr-1" />
              {t("header.call")}
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border animate-fade-in">
            <nav className="flex flex-col gap-4">
              {navItems.map((item) => (
                item.type === "link" ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="text-foreground hover:text-primary transition-smooth font-medium py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <a
                    key={item.name}
                    href={item.href}
                    className="text-foreground hover:text-primary transition-smooth font-medium py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </a>
                )
              ))}
              <div className="flex flex-col gap-2 pt-4 border-t border-border">
                <div className="flex gap-2">
                  <Button 
                    variant={language === "en" ? "default" : "outline"}
                    size="sm" 
                    className="flex-1"
                    onClick={() => setLanguage("en")}
                  >
                    🇬🇧 English
                  </Button>
                  <Button 
                    variant={language === "fi" ? "default" : "outline"}
                    size="sm" 
                    className="flex-1"
                    onClick={() => setLanguage("fi")}
                  >
                    🇫🇮 Suomi
                  </Button>
                </div>
                <a 
                  href="https://wa.me/358402401909"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  📱 WhatsApp
                </a>
                <Button variant="outline" size="sm" onClick={() => window.location.href = 'tel:0931588300'}>
                  <Phone className="h-4 w-4 mr-2" />
                  {t("header.call")}
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;